import React from 'react'

import { InterviewStatus, InterviewState } from '../../../api/gpt/types'
import { useI18N } from '../../../libraries/i18n/hook'
import ErrorMessage from '../errors/ErrorMessage'
import PlusIcon from '../../../assets/icons/plus.svg?react'
import Button from '../form/Button'
import ResultBox from './ResultBox'
import InterviewAiFeedback from '../../containers/user-feedback/InterviewAiFeedback'
import PageSection from '../layout/PageSection'
import InfoText from '../layout/InfoText'
import CyberPunkContainer from '../layout/full-screen/CyberPunkContainer'
import AudioQuestionBox from './AudioQuestionBox'

interface Props {
  interviewStatus: InterviewStatus | null
  evaluation?: {
    decision: boolean
    feedback: string
  } | null
  selectedTopic: string
  error: string | null
  onClearInterview: () => void
  goBackLink?: string
  description?: React.ReactNode
}

const AudioInterviewStatusRenderer: React.FC<Props> = ({
  interviewStatus,
  evaluation,
  selectedTopic,
  error,
  onClearInterview,
  goBackLink = '/ai-interview',
  description,
}) => {
  const { t } = useI18N()

  if (!interviewStatus) { return null }

  // Render interview in progress
  if (interviewStatus.state === InterviewState.ASKING_MAIN || interviewStatus.state === InterviewState.ASKING_FOLLOWUP) {
    return (
      <CyberPunkContainer>
        <div className='px-2 w-full max-w-5xl'>
          <PageSection
            showCloseIcon
            title='AI Interview'
            color='white'
            goBackLink={goBackLink}
          >
            {description}
            <AudioQuestionBox interviewStatus={interviewStatus} />
            <div className='ml-auto text-end max-w-lg'>
              <InterviewAiFeedback status={interviewStatus} interviewId={interviewStatus.id} question={interviewStatus.current_question} />
            </div>
            {error && <ErrorMessage message={error} />}
          </PageSection>

          <div className='bg-gray-100 text-gray-200 p-2 mt-auto mb-10 mx-auto max-w-lg rounded-md'>
            <InfoText text={t('speech.to.text.disclaimer')} iconColor='gray-200' />
          </div>
        </div>
      </CyberPunkContainer>
    )
  }

  // Render completed interview
  if (interviewStatus.state === InterviewState.COMPLETED && evaluation) {
    return (
      <>
        <div className='hidden lg:flex justify-end me-10 mt-4'>
          <Button icon={<PlusIcon />} onClick={onClearInterview} size='sm' outlined variant='secondary'>
            {t('interviewer.ai.new')}
          </Button>
        </div>
        <ResultBox evaluation={evaluation} topic={selectedTopic} />
        <div className='max-w-4xl mx-auto h-auto text-end'>
          <InterviewAiFeedback status={interviewStatus} interviewId={interviewStatus.id} />
        </div>
      </>
    )
  }

  return <>{`Invalid interview status ${interviewStatus.state}`}</>
}

export default AudioInterviewStatusRenderer
